'use client'
import {
  BarChart3,
  CreditCard,
  ExternalLink,
  ShieldCheck,
  User,
  Users,
  X,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FEATURE_FLAGS } from '@/constants/featureFlags'
import { useOrganizationFeatureFlag } from '@/hooks/useOrganizationFeatureFlag'
import { AccountSection } from './settings/AccountSection'
import { BillingSection } from './settings/BillingSection'
import { ConsentSection } from './settings/ConsentSection'
import { DeploymentSection } from './settings/DeploymentSection'
import { PageViewCaptureSection } from './settings/PageViewCaptureSection'
import { TeamSection } from './settings/TeamSection'
import { UsageSection } from './settings/UsageSection'

const data = {
  nav: [
    { name: 'Team', icon: Users, disabled: false },
    { name: 'Deployment', icon: ExternalLink, disabled: false },
    { name: 'Usage', icon: BarChart3, disabled: false },
    { name: 'Consent', icon: ShieldCheck, disabled: false },
    { name: 'Account', icon: User, disabled: true },
    { name: 'Billing', icon: CreditCard, disabled: true },
  ],
}

export function SettingsDialog({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: {
  children?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const [activeSection, setActiveSection] = useState('Team')
  const { enabled: pageViewCaptureEnabled } = useOrganizationFeatureFlag(
    FEATURE_FLAGS.PAGE_VIEW_CAPTURE
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="overflow-hidden p-0 md:max-h-[600px] md:max-w-[900px] lg:max-w-[1000px]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Customize your application settings here.
        </DialogDescription>
        <SidebarProvider className="items-start min-h-0">
          <div className="h-[580px] bg-secondary">
            <Sidebar
              collapsible="none"
              className="hidden md:flex bg-transparent h-full"
            >
              <SidebarContent>
                <SidebarGroup>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {data.nav.map((item) => (
                        <SidebarMenuItem key={item.name}>
                          {item.disabled ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="opacity-50 cursor-default">
                                    <SidebarMenuButton
                                      disabled
                                      className="cursor-default pointer-events-none"
                                    >
                                      <item.icon />
                                      <span>{item.name}</span>
                                    </SidebarMenuButton>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Coming soon</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <SidebarMenuButton
                              asChild
                              isActive={item.name === activeSection}
                            >
                              <button
                                type="button"
                                onClick={() => setActiveSection(item.name)}
                              >
                                <item.icon />
                                <span>{item.name}</span>
                              </button>
                            </SidebarMenuButton>
                          )}
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </SidebarContent>
            </Sidebar>
          </div>
          <main className="flex h-[580px] flex-1 flex-col overflow-hidden">
            {/* Breadcrumb */}
            <div className="bg-background px-6 py-[22px] flex items-center justify-between">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink className="text-muted-foreground">
                      Settings
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{activeSection}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <DialogClose className="ring-offset-background focus-visible:ring-ring/50 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-[3px] focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                <X />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
              {activeSection === 'Team' && <TeamSection />}
              {activeSection === 'Deployment' && <DeploymentSection />}
              {activeSection === 'Usage' && <UsageSection />}
              {activeSection === 'Consent' && (
                <>
                  <ConsentSection />
                  {pageViewCaptureEnabled && <PageViewCaptureSection />}
                </>
              )}
              {activeSection === 'Account' && <AccountSection />}
              {activeSection === 'Billing' && <BillingSection />}
            </div>
          </main>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  )
}
